import { load, save } from 'data/browserStorage';
import { LOCAL_STORAGE } from 'data/browserStorage/constants';

/**
 * Factory for localStorage-backed pin lists + persisted "All …" section expanded state.
 *
 * @param {object} config
 * @param {string} config.favoriteKeysPrefix Full key prefix before scope id, e.g. `workspace.project.favoriteActivityIds`
 * @param {string} config.allExpandedPrefix Full key prefix before scope id, e.g. `workspace.project.allActivitiesExpanded`
 * @param {string} config.favoriteKeysParseErrorLabel Console message when favorite keys JSON parse fails
 * @param {string} config.allExpandedParseErrorLabel Console message when expanded boolean parse fails
 */
export const makeLocalFavoriteStore = ({
  favoriteKeysPrefix,
  allExpandedPrefix,
  favoriteKeysParseErrorLabel,
  allExpandedParseErrorLabel,
}) => {
  const getFavoriteKeysStorageKey = (scopeId) => `${favoriteKeysPrefix}.${scopeId}`;
  const getAllExpandedStorageKey = (scopeId) => `${allExpandedPrefix}.${scopeId}`;

  const loadFavoriteKeys = (scopeId) => {
    if (!scopeId) return [];
    const raw = load(getFavoriteKeysStorageKey(scopeId), LOCAL_STORAGE);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.map((key) => (key != null ? String(key) : '')).filter(Boolean)
        : [];
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(favoriteKeysParseErrorLabel, e);
      return [];
    }
  };

  const saveFavoriteKeys = (scopeId, keys) => {
    if (!scopeId) return;
    save(getFavoriteKeysStorageKey(scopeId), JSON.stringify(keys), LOCAL_STORAGE);
  };

  const loadAllExpanded = (scopeId) => {
    if (!scopeId) return true;
    const raw = load(getAllExpandedStorageKey(scopeId), LOCAL_STORAGE);
    if (raw == null || raw === '') return true;
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === 'boolean' ? parsed : true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(allExpandedParseErrorLabel, e);
      return true;
    }
  };

  const saveAllExpanded = (scopeId, expanded) => {
    if (!scopeId) return;
    save(getAllExpandedStorageKey(scopeId), JSON.stringify(!!expanded), LOCAL_STORAGE);
  };

  return {
    getFavoriteKeysStorageKey,
    getAllExpandedStorageKey,
    loadFavoriteKeys,
    saveFavoriteKeys,
    loadAllExpanded,
    saveAllExpanded,
  };
};
